/// <amd-module name="Wit/SprintsHub/SprintsView/Components/SprintPicker" />
import { RouteParameters } from "Agile/Common/Constants/RoutingConstants";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Dropdown, DropdownCallout, DropdownExpandableButton, DropdownExpandableTextField, IDropdownCalloutProps } from "VSSUI/Dropdown";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IListBoxItem, ListBox } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ITableColumn } from "VSSUI/Table";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { SprintEditorDialog } from "Wit/Common/SprintEditorDialog/SprintEditorDialog";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { SprintPickerItem } from "./SprintPickerItem";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";

interface ISprintPickerItem {
    iterationData: IIterationData;
    pastOrFuture: boolean;
    pillText: string;
}

interface ISprintPickerProps {
    onChange?: (iteration?: IIterationData, iterationIsChanged?: boolean) => void;
}

const backslashRegex = /\\/g;

export class SprintPicker extends VssComponent<ISprintPickerProps> {
    // This public static method can be removed when deleting WebPlatform.WorkItemTracking.NewSprintsBacklogsView
    public static getSprintPickerMenuItem(onSprintChanged?: (iteration?: IIterationData) => void): IHeaderCommandBarItem {
        const sprintPickerMenuItem: IHeaderCommandBarItem = {
            className: "sprints-picker-menu-item",
            hideDropdownIcon: true,
            id: "sprints-picker-menu-item-id",
            renderButton: props => {
                return <SprintPicker key="sprint-picker-menu-button" onChange={onSprintChanged} />;
            },
            important: true,
            rank: 0,
            subtle: true
        };
        return sprintPickerMenuItem;
    }
    private isNewSprintsViewEnabled = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.NewSprintsBacklogsView, false);

    private teamSettings: IObservableValue<ITeamSettings | undefined>;
    private selection: DropdownSelection = new DropdownSelection();
    private listBoxRef?: React.RefObject<ListBox<{}>>;

    public constructor(props: ISprintPickerProps, context: IVssComponentContext) {
        super(props, context);

        const teamSettingsService = context.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        this.teamSettings = teamSettingsService.getTeamSettingsObservable();
        this.listBoxRef = React.createRef<ListBox<{}>>();
    }

    public render(): JSX.Element {
        return (
            <Observer teamSettings={this.teamSettings}>
                {(props: { teamSettings: ITeamSettings | undefined }) => {
                    if (props.teamSettings === undefined) {
                        return null;
                    }

                    const selectableValues: { values: IListBoxItem<ISprintPickerItem>[]; selectedIndex: number } = this.getSelectableValues(
                        props.teamSettings
                    );
                    this.selection.select(selectableValues.selectedIndex);

                    return (
                        <Dropdown
                            className="sprint-picker-dropdown"
                            actions={[{ text: Resources.NewSprint, iconProps: { iconName: "Add" }, onClick: this.openNewSprintDialog }]}
                            key={"sprint-picker-dropdown"}
                            items={selectableValues.values}
                            onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<ISprintPickerItem>) => {
                                const iterationIsChanged = this.isIterationChanged(item.id);
                                if (iterationIsChanged) {
                                    this.navigateToSprint(item.id);
                                }

                                this.props.onChange && this.props.onChange(item.data?.iterationData, iterationIsChanged);
                            }}
                            renderExpandable={props => (
                                this.isNewSprintsViewEnabled ? 
                                (
                                    <DropdownExpandableButton
                                        key="sprint-picker-dropdown-expandable"
                                        {...props}
                                        className={css(props.className, "padding-left-4")}
                                    />
                                ) : (
                                    <DropdownExpandableTextField
                                        key="sprint-picker-dropdown-expandable"
                                        {...props}
                                        className={css(props.className, "padding-left-4")}
                                    />
                                )
                            )}
                            role="menuitem"
                            showFilterBox={true}
                            filterByText={true}
                            renderItem={(
                                rowIndex: number,
                                columnIndex: number,
                                tableColumn: ITableColumn<IListBoxItem<ISprintPickerItem>>,
                                tableItem: IListBoxItem<ISprintPickerItem>
                            ) => (
                                <SprintPickerItem
                                    key={rowIndex}
                                    text={tableItem.text!}
                                    pillText={tableItem.data!.pillText}
                                    pastOrFuture={tableItem.data!.pastOrFuture}
                                />
                            )}
                            filterPlaceholderText={Resources.FilterSprints}
                            filteredNoResultsText={Resources.FilterNoSprints}
                            selection={this.selection}
                            minCalloutWidth={400}
                            renderCallout={this.renderCallout}
                            onExpand={this.onExpand}
                        />
                    );
                }}
            </Observer>
        );
    }

    private renderCallout = (props: IDropdownCalloutProps): JSX.Element => {
        const calloutProps = {
            ...props,
            listBoxRef: this.listBoxRef
        };
        return <DropdownCallout {...calloutProps} />;
    };

    private onExpand = (): void => {
        setTimeout(() => {
            if (this.selection?.value?.length) {
                this.listBoxRef?.current?.scrollIntoView(this.selection.value[0].beginIndex, {
                    block: "nearest"
                });
            }
        }, 0);
    };

    private isIterationChanged(iteration: string): boolean {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const currentIteration = historyService.getState().iteration;
        iteration = iteration.replace(backslashRegex, "/");
        if (iteration === currentIteration) {
            return false;
        }

        return true;
    }

    private navigateToSprint(iteration: string): void {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const routeValues: { [key: string]: string } = {
            [RouteParameters.Project]: historyService.getState().project,
            [RouteParameters.TeamName]: this.teamSettings.value!.teamName!,
            [RouteParameters.Iteration]: iteration,
            [RouteParameters.Pivot]: historyService.getState().pivot
        };

        historyService.pushState(routeValues);
    }

    private getSelectableValues(teamSettings: ITeamSettings): { values: IListBoxItem<ISprintPickerItem>[]; selectedIndex: number } {
        let selectableValues: IListBoxItem<ISprintPickerItem>[] = [];
        let selectedIndex = 0;
        if (teamSettings != undefined && teamSettings != undefined) {
            selectableValues = selectableValues.concat(
                teamSettings.previousIterations.map(i => this.createListBoxItem(i, true, Resources.SprintPast))
            );
            if (teamSettings.currentIteration) {
                selectableValues = selectableValues.concat(this.createListBoxItem(teamSettings.currentIteration, false, Resources.SprintCurrent));
            }
            selectableValues = selectableValues.concat(
                teamSettings.futureIterations.map(i => this.createListBoxItem(i, true, Resources.SprintFuture))
            );
        }

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const currentIteration = historyService.getState().iteration;
        selectedIndex = selectableValues.findIndex((v: IListBoxItem) => {
            return v.id == currentIteration;
        });
        if (selectedIndex === -1) {
            selectedIndex = 0;
        }

        return {
            values: selectableValues,
            selectedIndex: selectedIndex
        };
    }

    private createListBoxItem(iteration: IIterationData, pastOrFuture: boolean, pillText: string): IListBoxItem<ISprintPickerItem> {
        return {
            text: iteration.name,
            id: iteration.friendlyPath.replace(backslashRegex, "/"),
            data: {
                iterationData: iteration,
                pastOrFuture: pastOrFuture,
                pillText: pillText
            }
        };
    }

    private navigateToSprintOnComplete = (routeValues: { [key: string]: string }) => {
        const url = routeUrl(this.context.pageContext, "ms.vss-work-web.new-sprints-content-route", routeValues);
        onClickFPS(this.context.pageContext, url, true);
    };

    private openDialog(callout: (onDismiss: () => void) => JSX.Element): void {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(callout);
    }

    private openNewSprintDialog = () => {
        this.openDialog(onDismiss => {
            return (
                <SprintEditorDialog
                    onDismiss={onDismiss}
                    showTeamDropdown={false}
                    teamSettings={this.teamSettings}
                    navigateToSprintOnComplete={this.navigateToSprintOnComplete}
                />
            );
        });
    };
}

Components.add("sprint-picker", SprintPicker);
